import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop component ensures that when navigating between pages,
 * the scroll position is reset to the top of the page.
 * This provides a consistent user experience across the site.
 * 
 * This enhanced version uses multiple approaches to ensure it works
 * across all pages and in different browser environments.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Approach 1: Standard scroll reset
    window.scrollTo(0, 0);
    
    // Approach 2: Force scroll on document and body elements
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Approach 3: Try scrolling the specific main content area if it exists
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollTop = 0;
    }
    
    // Approach 4: Use requestAnimationFrame to ensure the scroll happens after rendering
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (mainContent) {
        mainContent.scrollTop = 0;
      }
    };
    
    // Execute immediately
    scrollToTop();
    
    // And also after a very short delay to ensure it happens after any layout changes
    requestAnimationFrame(scrollToTop);
    
    // And also with a slight timeout as a fallback
    const timeoutId = setTimeout(scrollToTop, 10);
    
    return () => clearTimeout(timeoutId);
  }, [pathname]);

  return null; // This component doesn't render anything
};

export default ScrollToTop;
